import { useMemo } from 'react';
import { useAuthStore } from '@store/auth.store';
import type { User } from '@/shared/types';

/** Minimal user shape needed for a permission check. */
type PermissionSubject = Pick<User, 'role'> & { permissions?: string[] };

/**
 * Plain permission check, usable outside React components.
 *
 * Semantics:
 *  - no key            → allowed (the surface is open to every authenticated user)
 *  - no user           → denied
 *  - role === 'ADMIN'  → always allowed, regardless of the permissions array
 *  - permissions array → allowed iff it contains the key
 *  - LEGACY FALLBACK   → user.permissions is undefined (stale session persisted
 *    before the RBAC release): allow, and let the backend's 403 be the gate.
 *    Never lock the UI harder than the API for old sessions — the profile sync
 *    on boot refreshes the array without forcing a re-login.
 */
export function can(user: PermissionSubject | null | undefined, key?: string): boolean {
  if (!key) return true;
  if (!user) return false;
  if (user.role === 'ADMIN') return true;
  if (user.permissions === undefined) return true; // legacy session fallback
  return user.permissions.includes(key);
}

/**
 * Permission-aware UI hook backed by the auth store.
 *
 * Usage:
 *   const { can } = usePermissions();
 *   if (can('planner.manage')) { ... }
 */
export function usePermissions() {
  const user = useAuthStore((s) => s.user);

  return useMemo(
    () => ({
      can: (key?: string) => can(user, key),
      permissions: user?.permissions ?? [],
      isAdmin: user?.role === 'ADMIN',
    }),
    [user],
  );
}
